/**
 * @fileoverview Unified Medical Image Analysis Component
 * This component provides an interface for analyzing medical images with tools for
 * image manipulation, annotation, and diagnostic analysis.
 *
 * @module Analysis
 */

import { useEffect, useState, useRef, useContext, useCallback } from "react"
import { Button, Col, Image, Row, Upload, Spin, message, Slider, Tooltip } from "antd"
import {
  PlusOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  ArrowRightOutlined,
  DeleteOutlined,
} from "@ant-design/icons"
import "../assets/styles/Analisis.css"
import { RightColumnRadiograph } from "../pages/RightColumnRadiograph"
import { RightColumnEcograph } from "../pages/RightColumnEcograph"
import { ImageContext } from "../contexts/imageContext/imageContext"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import imageCompression from "browser-image-compression"
import { useDropzone } from "react-dropzone"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import "../assets/styles/ImageToolbar.css"

const MAX_IMAGES = 10
const MAX_FILE_SIZE = 0.8 * 1024 * 1024 // 0.8MB en bytes
const MAX_TOTAL_SIZE = 4.5 * 1024 * 1024 // 4.5MB para dejar un margen de seguridad

/**
 * Calculates the total size of images in bytes
 *
 * @param {Array<string>} images - Array of base64 encoded images
 * @returns {number} Total size in bytes
 */
const calculateTotalSize = (images) => {
  return images.reduce((total, img) => {
    // Estimamos el tamaño de la imagen en base64
    return total + (img.length * 3) / 4
  }, 0)
}

/**
 * Draggable image component for the thumbnail sidebar
 *
 * @param {Object} props - Component props
 * @param {string} props.img - Image source (base64 or URL)
 * @param {number} props.index - Index of the image in the array
 * @param {Function} props.moveImage - Function to move an image
 * @param {Function} props.setSelectedImage - Function to set the selected image
 * @param {Function} props.setPosition - Function to set the image position
 * @param {Function} props.setZoom - Function to set the zoom level
 * @param {Function} props.removeImage - Function to remove an image
 * @param {boolean} props.isRadiograph - Whether the analysis is for radiographs
 * @returns {JSX.Element} Draggable image component
 */
const DraggableImage = ({
  img,
  index,
  moveImage,
  setSelectedImage,
  setPosition,
  setZoom,
  removeImage,
  isRadiograph,
}) => {
  return (
    <Draggable draggableId={img} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`image-thumbnail ${snapshot.isDragging ? "dragging" : ""}`}
        >
          <img
            src={img || "/placeholder.svg"}
            alt={`Thumbnail ${index}`}
            onClick={() => {
              setSelectedImage(img)
              if (isRadiograph) {
                setPosition({ x: 0, y: 0 })
                setZoom(1)
              }
            }}
            className="thumbnail-image"
          />
          <Button
            icon={<PlusOutlined style={{ transform: "rotate(45deg)" }} />}
            onClick={(e) => {
              e.stopPropagation()
              removeImage(index)
            }}
            className="delete-image-button"
          />
        </div>
      )}
    </Draggable>
  )
}

/**
 * Main component for medical image analysis
 * Provides an interface for viewing, manipulating, and analyzing medical images
 *
 * @param {Object} props - Component props
 * @param {string} props.analysisType - Type of analysis ('radiograph' or 'ecograph')
 * @returns {JSX.Element} Analysis component
 */
const Analysis = ({ analysisType = "radiograph" }) => {
  const isRadiograph = analysisType === "radiograph"
  const { images, reorderImages, addImages, removeImage } = useContext(ImageContext)
  const [selectedImage, setSelectedImage] = useState(null)
  const [zoom, setZoom] = useState(1)
  const [invert, setInvert] = useState(0)
  const [contrast, setContrast] = useState(100)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 })
  const [regenerateCount, setRegenerateCount] = useState(() => {
    const savedCount = sessionStorage.getItem("regenerateCount")
    return savedCount ? Number.parseInt(savedCount, 10) : 0
  })
  const [isDrawingMode, setIsDrawingMode] = useState(false)
  const [arrowsByImage, setArrowsByImage] = useState({})
  const [currentArrow, setCurrentArrow] = useState(null)
  const [isDrawingArrow, setIsDrawingArrow] = useState(false)
  const [isEraserMode, setIsEraserMode] = useState(false)
  const [rightColumnWidth, setRightColumnWidth] = useState(400)
  const [isDraggingResize, setIsDraggingResize] = useState(false)
  const [isWidthLoaded, setIsWidthLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isCentralColumnCollapsed, setIsCentralColumnCollapsed] = useState(false)
  const [windowWidth, setWindowWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200)
  const dragStartX = useRef(0)
  const dragStartWidth = useRef(0)
  const canvasRef = useRef(null)
  const { t } = useTranslation()
  const navigate = useNavigate()

  /**
   * Check if the study type is correct, redirect if not
   */
  useEffect(() => {
    try {
      const formValuesString = sessionStorage.getItem("formValues")

      if (formValuesString) {
        const formValues = JSON.parse(formValuesString)

        if (isRadiograph && formValues.tipo_estudio === "Ecografía") {
          navigate("/ecografia")
        } else if (!isRadiograph && formValues.tipo_estudio === "Radiografía") {
          navigate("/radiografia")
        }
      }
    } catch (error) {
      console.error("Error checking session storage:", error)
    }
  }, [navigate, isRadiograph])

  /**
   * Set the first image as selected when images are loaded
   */
  useEffect(() => {
    if (images.length > 0) {
      setSelectedImage(images[0])
    }
  }, [images])

  /**
   * Save regenerate count to session storage when it changes
   */
  useEffect(() => {
    sessionStorage.setItem("regenerateCount", regenerateCount.toString())
  }, [regenerateCount])

  /**
   * Increase zoom level
   */
  const handleZoomIn = () => {
    setZoom((prevZoom) => Math.min(prevZoom + 0.1, 3))
  }

  /**
   * Decrease zoom level
   */
  const handleZoomOut = () => {
    setZoom((prevZoom) => Math.max(prevZoom - 0.1, 0.5))
  }

  /**
   * Handle mouse down event for image dragging
   *
   * @param {React.MouseEvent} e - Mouse event
   */
  const handleMouseDown = (e) => {
    if (!isRadiograph) return
    e.preventDefault()
    setDragging(true)
    setStartPosition({ x: e.clientX - position.x, y: e.clientY - position.y })
  }

  /**
   * Handle mouse move event for image dragging
   *
   * @param {React.MouseEvent} e - Mouse event
   */
  const handleMouseMove = (e) => {
    if (!isRadiograph || !dragging) return

    const newX = e.clientX - startPosition.x
    const newY = e.clientY - startPosition.y

    const limitedX = Math.min(Math.max(newX, -((zoom - 1) * 400)), (zoom - 1) * 400)
    const limitedY = Math.min(Math.max(newY, -((zoom - 1) * 296)), (zoom - 1) * 296)

    setPosition({ x: limitedX, y: limitedY })
  }

  /**
   * Handle mouse up event for image dragging
   */
  const handleMouseUp = () => {
    if (isRadiograph) {
      setDragging(false)
    }
  }

  /**
   * Navigate to the previous image in the list
   */
  const handlePreviousImage = () => {
    if (images.length <= 1) return
    const currentIndex = images.findIndex((img) => img === selectedImage)
    const prevIndex = currentIndex <= 0 ? images.length - 1 : currentIndex - 1
    setSelectedImage(images[prevIndex])
    if (isRadiograph) {
      setPosition({ x: 0, y: 0 })
      setZoom(1)
    }
  }

  /**
   * Navigate to the next image in the list
   */
  const handleNextImage = () => {
    if (images.length <= 1) return
    const currentIndex = images.findIndex((img) => img === selectedImage)
    const nextIndex = currentIndex >= images.length - 1 ? 0 : currentIndex + 1
    setSelectedImage(images[nextIndex])
    if (isRadiograph) {
      setPosition({ x: 0, y: 0 })
      setZoom(1)
    }
  }

  /**
   * Handle canvas click for drawing arrows or erasing
   *
   * @param {React.MouseEvent} e - Mouse event
   */
  const handleCanvasClick = (e) => {
    if (!isRadiograph || (!isDrawingMode && !isEraserMode)) return
    if (!canvasRef.current) return

    const rect = canvasRef.current.getBoundingClientRect()
    const x = (e.clientX - rect.left - position.x) / zoom
    const y = (e.clientY - rect.top - position.y) / zoom

    if (isDrawingMode) {
      if (!isDrawingArrow) {
        setCurrentArrow({ start: { x, y } })
        setIsDrawingArrow(true)
      } else {
        const newArrow = { ...currentArrow, end: { x, y } }
        setArrowsByImage((prevArrows) => ({
          ...prevArrows,
          [selectedImage]: [...(prevArrows[selectedImage] || []), newArrow],
        }))
        setCurrentArrow(null)
        setIsDrawingArrow(false)
      }
    } else if (isEraserMode) {
      eraseArrowsNear(x, y)
    }
  }

  /**
   * Handle canvas mouse move for drawing arrows
   *
   * @param {React.MouseEvent} e - Mouse event
   */
  const handleCanvasMouseMove = (e) => {
    if (!isRadiograph || !isDrawingMode || !isDrawingArrow || !currentArrow || !canvasRef.current) return

    const rect = canvasRef.current.getBoundingClientRect()
    const x = (e.clientX - rect.left - position.x) / zoom
    const y = (e.clientY - rect.top - position.y) / zoom

    setCurrentArrow((prev) => ({ ...prev, end: { x, y } }))
  }

  /**
   * Erase arrows near a point
   *
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   */
  const eraseArrowsNear = (x, y) => {
    const eraserRadius = 10
    setArrowsByImage((prevArrows) => {
      const currentImageArrows = prevArrows[selectedImage] || []
      const remainingArrows = currentImageArrows.filter((arrow) => {
        return !isPointNearLine(x, y, arrow.start, arrow.end, eraserRadius)
      })
      return {
        ...prevArrows,
        [selectedImage]: remainingArrows,
      }
    })
  }

  /**
   * Check if a point is near a line
   *
   * @param {number} x - Point X coordinate
   * @param {number} y - Point Y coordinate
   * @param {Object} start - Line start point
   * @param {Object} end - Line end point
   * @param {number} maxDistance - Maximum distance to consider a point near the line
   * @returns {boolean} True if the point is near the line
   */
  const isPointNearLine = (x, y, start, end, maxDistance) => {
    const A = x - start.x
    const B = y - start.y
    const C = end.x - start.x
    const D = end.y - start.y

    const dot = A * C + B * D
    const lenSq = C * C + D * D
    let param = -1
    if (lenSq !== 0) {
      param = dot / lenSq
    }

    let xx, yy

    if (param < 0) {
      xx = start.x
      yy = start.y
    } else if (param > 1) {
      xx = end.x
      yy = end.y
    } else {
      xx = start.x + param * C
      yy = start.y + param * D
    }

    const dx = x - xx
    const dy = y - yy
    const distance = Math.sqrt(dx * dx + dy * dy)

    return distance <= maxDistance
  }

  /**
   * Draw arrows on the canvas
   */
  const drawArrows = useCallback(() => {
    if (!isRadiograph) return

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext("2d")
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const drawArrow = (start, end, color) => {
      const headlen = 10 / zoom
      const dx = end.x - start.x
      const dy = end.y - start.y
      const angle = Math.atan2(dy, dx)

      ctx.beginPath()
      ctx.moveTo(start.x, start.y)
      ctx.lineTo(end.x, end.y)
      ctx.strokeStyle = color
      ctx.lineWidth = 2 / zoom
      ctx.stroke()

      ctx.beginPath()
      ctx.moveTo(end.x, end.y)
      ctx.lineTo(end.x - headlen * Math.cos(angle - Math.PI / 6), end.y - headlen * Math.sin(angle - Math.PI / 6))
      ctx.lineTo(end.x - headlen * Math.cos(angle + Math.PI / 6), end.y - headlen * Math.sin(angle + Math.PI / 6))
      ctx.fillStyle = color
      ctx.fill()
    }

    const currentImageArrows = arrowsByImage[selectedImage] || []
    currentImageArrows.forEach((arrow, index) => {
      const start = {
        x: arrow.start.x * zoom + position.x,
        y: arrow.start.y * zoom + position.y,
      }
      const end = {
        x: arrow.end.x * zoom + position.x,
        y: arrow.end.y * zoom + position.y,
      }

      const color = index % 2 === 0 ? "#FF6F00" : "#00FF00"
      drawArrow(start, end, color)
    })

    if (currentArrow && currentArrow.end) {
      const start = {
        x: currentArrow.start.x * zoom + position.x,
        y: currentArrow.start.y * zoom + position.y,
      }
      const end = {
        x: currentArrow.end.x * zoom + position.x,
        y: currentArrow.end.y * zoom + position.y,
      }

      const color = currentImageArrows.length % 2 === 0 ? "#FFCC80" : "#28a745"
      drawArrow(start, end, color)
    }
  }, [isRadiograph, arrowsByImage, zoom, position, currentArrow, selectedImage])

  /**
   * Handle contrast slider change
   *
   * @param {number} value - New contrast value
   */
  const handleContrastChange = (value) => {
    setContrast(value)
  }

  /**
   * Handle invert slider change
   *
   * @param {number} value - New invert value
   */
  const handleInvertChange = (value) => {
    setInvert(value)
  }

  /**
   * Draw arrows when canvas, arrows, zoom, or position changes
   */
  useEffect(() => {
    if (isRadiograph && canvasRef.current) {
      drawArrows()
    }
  }, [drawArrows, isRadiograph])

  /**
   * Toggle drawing mode
   */
  const handleDrawArrow = () => {
    setIsDrawingMode(!isDrawingMode)
    setIsEraserMode(false)
    setIsDrawingArrow(false)
    setCurrentArrow(null)
  }

  /**
   * Toggle eraser mode
   */
  const handleEraser = () => {
    setIsEraserMode(!isEraserMode)
    setIsDrawingMode(false)
    setIsDrawingArrow(false)
    setCurrentArrow(null)
  }

  /**
   * Handle resize start for the right column
   *
   * @param {React.MouseEvent} e - Mouse event
   */
  const handleResizeStart = (e) => {
    if (isCentralColumnCollapsed) return
    e.preventDefault()
    setIsDraggingResize(true)
    dragStartX.current = e.clientX
    dragStartWidth.current = rightColumnWidth
  }

  /**
   * Handle resize for the right column
   *
   * @param {MouseEvent} e - Mouse event
   */
  const handleResize = useCallback(
    (e) => {
      if (!isDraggingResize || isCentralColumnCollapsed) return
      const deltaX = dragStartX.current - e.clientX
      const newWidth = Math.max(350, Math.min(600, dragStartWidth.current + deltaX))
      setRightColumnWidth(newWidth)
      localStorage.setItem("rightColumnWidth", newWidth.toString())
    },
    [isDraggingResize, isCentralColumnCollapsed],
  )

  /**
   * Handle resize end for the right column
   */
  const handleResizeEnd = useCallback(() => {
    setIsDraggingResize(false)
  }, [])

  /**
   * Add and remove resize event listeners
   */
  useEffect(() => {
    if (isDraggingResize) {
      window.addEventListener("mousemove", handleResize)
      window.addEventListener("mouseup", handleResizeEnd)
    }
    return () => {
      window.removeEventListener("mousemove", handleResize)
      window.removeEventListener("mouseup", handleResizeEnd)
    }
  }, [isDraggingResize, handleResize, handleResizeEnd])

  /**
   * Load saved column width from localStorage
   */
  useEffect(() => {
    const savedWidth = localStorage.getItem("rightColumnWidth")
    const initialWidth = savedWidth ? Number.parseInt(savedWidth, 10) : 400
    setRightColumnWidth(initialWidth)
    setIsWidthLoaded(true)
  }, [])

  /**
   * Handle window resize events
   */
  useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(window.innerWidth)
      // Force re-render when window is resized to update responsive layout
      setIsWidthLoaded(false)
      setTimeout(() => setIsWidthLoaded(true), 0)
    }

    window.addEventListener("resize", handleWindowResize)
    return () => window.removeEventListener("resize", handleWindowResize)
  }, [])

  /**
   * Handle drag and drop reordering of images
   *
   * @param {Object} result - Drag and drop result object
   */
  const onDragEnd = (result) => {
    if (!result.destination) {
      return
    }

    const newImages = Array.from(images)
    const [reorderedItem] = newImages.splice(result.source.index, 1)
    newImages.splice(result.destination.index, 0, reorderedItem)
    reorderImages(newImages)
  }

  /**
   * Process and add new images to the collection
   *
   * @param {Array<File>} files - Array of image files to process
   */
  const handleImagenesChange = useCallback(
    async (files) => {
      setIsLoading(true)
      const validFiles = files.filter(
        (file) => file.type.startsWith("image/") && !/\.(webp|svg|gif)$/i.test(file.name) && file.size <= MAX_FILE_SIZE,
      )

      if (validFiles.length === 0) {
        message.error(t("analysisPage.formatError"))
        setIsLoading(false)
        return
      }

      if (images.length + validFiles.length > MAX_IMAGES) {
        message.error(t("analysisPage.imageLimit"))
        setIsLoading(false)
        return
      }

      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      }

      const compressedImagePromises = validFiles.map(async (file) => {
        try {
          const compressedFile = await imageCompression(file, options)
          return await readFileAsDataURL(compressedFile)
        } catch (error) {
          console.error("Error al comprimir la imagen:", error)
          message.error(t("analysisPage.compressionError"))
          return null
        }
      })

      const compressedImages = await Promise.all(compressedImagePromises)
      const filteredImages = compressedImages.filter((img) => img)

      const currentTotalSize = calculateTotalSize(images)
      const newTotalSize = calculateTotalSize(filteredImages)

      if (currentTotalSize + newTotalSize > MAX_TOTAL_SIZE) {
        message.warning(t("analysisPage.storageLimit"))
        setIsLoading(false)
        return
      }

      addImages(filteredImages)
      setIsLoading(false)
    },
    [addImages, images, t],
  )

  /**
   * Convert a file to a data URL
   *
   * @param {File} file - File to convert
   * @returns {Promise<string>} Promise resolving to data URL
   */
  const readFileAsDataURL = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
    })
  }

  /**
   * Handle file drop event from dropzone
   *
   * @param {Array<File>} acceptedFiles - Files dropped into the dropzone
   */
  const onDrop = useCallback(
    (acceptedFiles) => {
      handleImagenesChange(acceptedFiles)
    },
    [handleImagenesChange],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*",
    noClick: true,
  })

  /**
   * Handle adding a single image from the upload component
   *
   * @param {Object} param0 - Upload info object
   * @param {Object} param0.file - File object
   */
  const handleAddImage = async ({ file }) => {
    const fileObj = file.originFileObj || file
    try {
      const dataUrl = await readFileAsDataURL(fileObj)
      const isImageAlreadyAdded = images.some((image) => image === dataUrl)

      if (!isImageAlreadyAdded) {
        addImages([dataUrl])
      }
    } catch (error) {
      console.error("Error al convertir el archivo a data URL:", error)
    }
  }

  /**
   * Toggle the central column collapsed state
   */
  const toggleCentralColumn = () => {
    setIsCentralColumnCollapsed(!isCentralColumnCollapsed)
  }

  const isLargeScreen = windowWidth > 992

  // Determine which right column component to use
  const RightColumnComponent = isRadiograph ? RightColumnRadiograph : RightColumnEcograph

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div {...getRootProps()} className="analysis-container" onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
        <input {...getInputProps()} />
        {isDragActive && (
          <div className="drag-overlay">
            <p>{t("analysisPage.dropImages")}</p>
          </div>
        )}
        <Row className="content content2">
          <Col md={2} sm={24} xs={24} className="columna-izq">
            <Droppable droppableId="image-list" direction="vertical">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {images.map((img, index) => (
                    <DraggableImage
                      key={img}
                      img={img}
                      index={index}
                      moveImage={onDragEnd}
                      setSelectedImage={setSelectedImage}
                      setPosition={setPosition}
                      setZoom={setZoom}
                      removeImage={removeImage}
                      isRadiograph={isRadiograph}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
            <Upload
              accept="image/*"
              multiple
              showUploadList={false}
              listType="picture"
              beforeUpload={() => false}
              customRequest={({ file, onSuccess }) => {
                setTimeout(() => {
                  onSuccess("ok", file)
                }, 0)
              }}
              onChange={handleAddImage}
              disabled={images.length >= MAX_IMAGES || isLoading}
            >
              {isLoading ? (
                <Spin />
              ) : (
                <Button
                  icon={<PlusOutlined />}
                  className="add-image-button"
                  disabled={images.length >= MAX_IMAGES || isLoading}
                />
              )}
            </Upload>
          </Col>
          <Col
            className={`columna-cen ${isCentralColumnCollapsed ? "collapsed" : ""}`}
            style={
              isWidthLoaded
                ? {
                  width: isCentralColumnCollapsed ? "0" : `calc(100% - 16.66% - ${rightColumnWidth}px)`,
                  overflow: "hidden",
                  transition: "width 0.3s ease-in-out",
                }
                : {}
            }
          >
            {!isCentralColumnCollapsed && (
              <>
                {isLoading ? (
                  <div className="loading-container">
                    <Spin size="large" tip={t("analysisPage.loading")} />
                  </div>
                ) : images.length > 0 ? (
                  <div className="image-container">
                    {isRadiograph && (
                      <div className="image-toolbar">
                        <div className="image-toolbar-section">
                          {/* <h4 className="image-toolbar-title">Zoom {Math.round(zoom * 100)}%</h4> */}
                          <div className="image-toolbar-buttons">
                            <Tooltip title="Acercar">
                              <Button
                                icon={<ZoomInOutlined />}
                                onClick={handleZoomIn}
                                className="image-toolbar-button"
                                aria-label="Acercar"
                              />
                            </Tooltip>
                            <Tooltip title="Alejar">
                              <Button
                                icon={<ZoomOutOutlined />}
                                onClick={handleZoomOut}
                                className="image-toolbar-button"
                                aria-label="Alejar"
                              />
                            </Tooltip>
                          </div>
                        </div>

                        <div className="image-toolbar-section">
                          {/* <h4 className="image-toolbar-title">Herramientas</h4> */}
                          <div className="image-toolbar-buttons">
                            <Tooltip title="Dibujar flecha">
                              <Button
                                icon={<ArrowRightOutlined />}
                                onClick={handleDrawArrow}
                                className={`image-toolbar-button ${isDrawingMode ? "active" : ""}`}
                                aria-label={
                                  isDrawingMode
                                    ? "Desactivar modo de dibujo de flechas"
                                    : "Activar modo de dibujo de flechas"
                                }
                              />
                            </Tooltip>
                            <Tooltip title="Borrador">
                              <Button
                                icon={<DeleteOutlined />}
                                onClick={handleEraser}
                                className={`image-toolbar-button ${isEraserMode ? "active" : ""}`}
                                aria-label={isEraserMode ? "Desactivar goma de borrar" : "Activar goma de borrar"}
                              />
                            </Tooltip>
                          </div>
                        </div>

                        <div className="image-toolbar-sliders">
                          {/* <h4 className="image-toolbar-title"> </h4> */}
                          <div className="image-toolbar-slider-group">
                            <div className="image-toolbar-slider-container">
                              <h4 className="image-toolbar-title">Contraste: {contrast}%</h4>
                              <Slider
                                min={100}
                                max={500}
                                value={contrast}
                                onChange={handleContrastChange}
                                className="image-toolbar-slider"
                              />
                            </div>
                            <div className="image-toolbar-slider-container">
                              <h4 className="image-toolbar-title">Invertir: {invert}%</h4>
                              <Slider
                                min={0}
                                max={100}
                                value={invert}
                                onChange={handleInvertChange}
                                className="image-toolbar-slider"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div
                      className="image-wrapper"
                      onMouseDown={isRadiograph ? handleMouseDown : undefined}
                      onClick={isRadiograph ? handleCanvasClick : undefined}
                      onMouseMove={isRadiograph ? handleCanvasMouseMove : undefined}
                      style={{
                        cursor: isRadiograph
                          ? isDrawingMode
                            ? "crosshair"
                            : isEraserMode
                              ? "cell"
                              : zoom > 1
                                ? dragging
                                  ? "grabbing"
                                  : "grab"
                                : "default"
                          : "default",
                      }}
                    >
                      {images.length > 1 && (
                        <>
                          <Button
                            icon={<LeftOutlined />}
                            onClick={(e) => {
                              e.stopPropagation()
                              handlePreviousImage()
                            }}
                            className="navigation-arrow navigation-arrow-left"
                            aria-label={t("analysisPage.previousImage")}
                          />
                          <Button
                            icon={<RightOutlined />}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleNextImage()
                            }}
                            className="navigation-arrow navigation-arrow-right"
                            aria-label={t("analysisPage.nextImage")}
                          />
                        </>
                      )}
                      {(selectedImage || images[0]) && (
                        <Image
                          src={selectedImage || images[0]}
                          alt="Selected"
                          preview={false}
                          className="full-image no-select"
                          style={
                            isRadiograph
                              ? {
                                transform: `scale(${zoom}) translate(${position.x}px, ${position.y}px)`,
                                filter: `contrast(${contrast}%) invert(${invert}%)`,
                              }
                              : undefined
                          }
                        />
                      )}
                      <canvas
                        ref={canvasRef}
                        width={800}
                        height={592}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          pointerEvents: "none",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <p className="default-text">{t("analysisPage.noImages")}</p>
                )}
              </>
            )}
          </Col>

          <div
            className="columna-der-resizable"
            style={
              isWidthLoaded
                ? {
                  width: isCentralColumnCollapsed
                    ? isLargeScreen
                      ? "calc(100% - 8.332%)" // For large screens when collapsed, take full width minus left column
                      : "calc(100% - 8.332%)" // For small screens when collapsed, take full width minus left column
                    : `${rightColumnWidth}px`,
                  transition: "width 0.3s ease-in-out",
                }
                : {}
            }
          >
            <div
              className={`resize-handle ${isCentralColumnCollapsed ? "disabled" : ""}`}
              onMouseDown={handleResizeStart}
            ></div>
            <Button
              icon={isCentralColumnCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={toggleCentralColumn}
              className="toggle-central-column"
            />
            <RightColumnComponent
              regenerateCount={regenerateCount}
              setRegenerateCount={setRegenerateCount}
              setIsModalVisible={() => { }}
              width={isCentralColumnCollapsed ? "100%" : rightColumnWidth}
              isCentralColumnCollapsed={isCentralColumnCollapsed}
              isLargeScreen={isLargeScreen}
            />
          </div>
        </Row>
      </div>
    </DragDropContext>
  )
}

export default Analysis
